export interface OnboardingLangStrings {
    // Navigation
    next: string;
    back: string;
    continue: string;
    finish: string;
    cancel: string;

    // Steps
    welcome: string;
    cvUpload: string;
    analysis: string;
    results: string;
    overview: string;
    completion: string;

    // Personal Info
    personalInfo: string;
    firstName: string;
    lastName: string;
    shortDescription: string;
    shortDescriptionPlaceholder: string;

    // Skills & Competences
    skills: string;
    competences: string;
    certificates: string;
    roles: string;
    suggestedSkills: string;
    suggestedCompetences: string;
    suggestedCertificates: string;
    suggestedRoles: string;

    // Levels
    junior: string;
    midLevel: string;
    senior: string;
    expert: string;

    // Actions
    accept: string;
    reject: string;
    edit: string;
    add: string;
    close: string;
    save: string;
    confirm: string;

    // Messages
    noSkillsSelected: string;
    noCompetencesSelected: string;
    noCertificatesSelected: string;
    noRolesSelected: string;

    // Agent Messages
    welcomeMessage: string;
    cvUploadMessage: string;
    personalInfoMessage: string;
    skillsMessage: string;
    superPowersMessage: string;
    certificatesMessage: string;
    rolesMessage: string;
    resultsMessage: string;

    // Tooltips & Aria Labels
    editSkillLevel: string;
    acceptItem: string;
    rejectItem: string;
    uploadLogo: string;
    selectDate: string;
    expandSection: string;
    collapseSection: string;

    // Super Powers
    superPowers: string;
    topSkills: string;
    noSuperPowers: string;

    // Progress
    progress: string;
    step: string;
    of: string;

    // OnboardingStatusWidget
    onboardingStart: string;
    onboardingStartDescription: string;
    onboardingContinue: string;
    onboardingContinueDescription: string;
    onboardingCompleted: string;
    onboardingCompletedDescription: string;
    startNow: string;
    continueOnboarding: string;
    viewResults: string;
    estimatedDuration: string;
    pauseAnytime: string;
}

export const onboardingStrings: Record<'de' | 'en' | 'th', OnboardingLangStrings> = {
    de: {
        // Navigation
        next: 'Weiter',
        back: 'Zurück',
        continue: 'Fortfahren',
        finish: 'Fertigstellen',
        cancel: 'Abbrechen',

        // Steps
        welcome: 'Willkommen',
        cvUpload: 'CV Upload',
        analysis: 'Analyse',
        results: 'Ergebnisse',
        overview: 'Übersicht',
        completion: 'Fertigstellung',

        // Personal Info
        personalInfo: 'Persönliche Angaben',
        firstName: 'Vorname',
        lastName: 'Nachname',
        shortDescription: 'Kurzbeschreibung',
        shortDescriptionPlaceholder: 'Beschreiben Sie sich kurz in 2-3 Sätzen',

        // Skills & Competences
        skills: 'Fähigkeiten',
        competences: 'Kompetenzen',
        certificates: 'Zertifikate',
        roles: 'Rollen',
        suggestedSkills: 'Vorgeschlagene Fähigkeiten',
        suggestedCompetences: 'Vorgeschlagene Kompetenzen',
        suggestedCertificates: 'Vorgeschlagene Zertifikate',
        suggestedRoles: 'Vorgeschlagene Rollen',

        // Levels
        junior: 'Junior',
        midLevel: 'Mid-Level',
        senior: 'Senior',
        expert: 'Expert',

        // Actions
        accept: 'Annehmen',
        reject: 'Ablehnen',
        edit: 'Bearbeiten',
        add: 'Hinzufügen',
        close: 'Schließen',
        save: 'Speichern',
        confirm: 'Bestätigen',

        // Messages
        noSkillsSelected: 'Keine Fähigkeiten ausgewählt',
        noCompetencesSelected: 'Keine Kompetenzen ausgewählt',
        noCertificatesSelected: 'Keine Zertifikate ausgewählt',
        noRolesSelected: 'Keine Rollen ausgewählt',

        // Agent Messages
        welcomeMessage: 'Willkommen beim Onboarding! Ich begleite Sie durch den Prozess.',
        cvUploadMessage: 'Laden Sie Ihren Lebenslauf hoch, damit wir Ihre Fähigkeiten analysieren können.',
        personalInfoMessage:
            'Beginnen wir mit Ihren persönlichen Angaben. Bitte vervollständigen Sie Ihren Namen und fügen Sie eine kurze Beschreibung hinzu.',
        skillsMessage:
            'Großartig! Nun schauen wir uns die identifizierten Fähigkeiten an. Überprüfen Sie die Vorschläge und passen Sie die Skill-Level nach Ihrem Können an.',
        superPowersMessage:
            'Perfekt! Jetzt wählen Sie Ihre SuperPowers aus. Wählen Sie bis zu 3 Ihrer stärksten Fähigkeiten aus, die Sie besonders auszeichnen.',
        certificatesMessage:
            'Sehr gut! Hier sind die erkannten Zertifikate. Bestätigen Sie die relevanten und fügen Sie weitere hinzu, falls nötig.',
        rolesMessage:
            'Ausgezeichnet! Zum Abschluss die vorgeschlagenen Rollen. Diese basieren auf Ihren Fähigkeiten und Erfahrungen.',
        resultsMessage:
            'Fantastisch! Hier ist deine persönliche Talent-Übersicht. Deine Super Powers sind hervorgehoben. Du kannst noch Anpassungen vornehmen, bevor wir fortfahren.',

        // Tooltips & Aria Labels
        editSkillLevel: 'Skill-Level bearbeiten',
        acceptItem: 'Element annehmen',
        rejectItem: 'Element ablehnen',
        uploadLogo: 'Logo hochladen',
        selectDate: 'Datum auswählen',
        expandSection: 'Bereich erweitern',
        collapseSection: 'Bereich einklappen',

        // Super Powers
        superPowers: 'Super Powers',
        topSkills: 'Top Fähigkeiten',
        noSuperPowers: 'Keine Super Powers identifiziert. Bewerten Sie Ihre Skills höher, um sie hier zu sehen.',

        // Progress
        progress: 'Fortschritt',
        step: 'Schritt',
        of: 'von',

        // OnboardingStatusWidget
        onboardingStart: 'Onboarding starten',
        onboardingStartDescription: 'Willkommen! Starte dein persönliches Onboarding.',
        onboardingContinue: 'Onboarding fortsetzen',
        onboardingContinueDescription: 'Du hast {progress}% deines Onboardings abgeschlossen.',
        onboardingCompleted: 'Onboarding abgeschlossen',
        onboardingCompletedDescription: 'Erfolgreich abgeschlossen',
        startNow: 'Jetzt starten',
        continueOnboarding: 'Fortsetzen',
        viewResults: 'Ergebnisse ansehen',
        estimatedDuration: 'Geschätzte Dauer: ~11 Minuten',
        pauseAnytime: 'Du kannst jederzeit pausieren und später fortfahren',
    },

    en: {
        // Navigation
        next: 'Next',
        back: 'Back',
        continue: 'Continue',
        finish: 'Finish',
        cancel: 'Cancel',

        // Steps
        welcome: 'Welcome',
        cvUpload: 'CV Upload',
        analysis: 'Analysis',
        results: 'Results',
        overview: 'Overview',
        completion: 'Completion',

        // Personal Info
        personalInfo: 'Personal Information',
        firstName: 'First Name',
        lastName: 'Last Name',
        shortDescription: 'Short Description',
        shortDescriptionPlaceholder: 'Describe yourself briefly in 2-3 sentences',

        // Skills & Competences
        skills: 'Skills',
        competences: 'Competences',
        certificates: 'Certificates',
        roles: 'Roles',
        suggestedSkills: 'Suggested Skills',
        suggestedCompetences: 'Suggested Competences',
        suggestedCertificates: 'Suggested Certificates',
        suggestedRoles: 'Suggested Roles',

        // Levels
        junior: 'Junior',
        midLevel: 'Mid-Level',
        senior: 'Senior',
        expert: 'Expert',

        // Actions
        accept: 'Accept',
        reject: 'Reject',
        edit: 'Edit',
        add: 'Add',
        close: 'Close',
        save: 'Save',
        confirm: 'Confirm',

        // Messages
        noSkillsSelected: 'No skills selected',
        noCompetencesSelected: 'No competences selected',
        noCertificatesSelected: 'No certificates selected',
        noRolesSelected: 'No roles selected',

        // Agent Messages
        welcomeMessage: 'Welcome to onboarding! I will guide you through the process.',
        cvUploadMessage: 'Upload your CV so we can analyze your skills.',
        personalInfoMessage:
            "Let's start with your personal information. Please complete your name and add a short description.",
        skillsMessage:
            "Great! Now let's look at the identified skills. Review the suggestions and adjust the skill levels according to your abilities.",
        superPowersMessage:
            'Perfect! Now choose your SuperPowers. Select up to 3 of your strongest skills that particularly distinguish you.',
        certificatesMessage:
            'Very good! Here are the recognized certificates. Confirm the relevant ones and add more if necessary.',
        rolesMessage: 'Excellent! Finally, the suggested roles. These are based on your skills and experience.',
        resultsMessage:
            'Fantastic! Here is your personal talent overview. Your Super Powers are highlighted. You can still make adjustments before we continue.',

        // Tooltips & Aria Labels
        editSkillLevel: 'Edit skill level',
        acceptItem: 'Accept item',
        rejectItem: 'Reject item',
        uploadLogo: 'Upload logo',
        selectDate: 'Select date',
        expandSection: 'Expand section',
        collapseSection: 'Collapse section',

        // Super Powers
        superPowers: 'Super Powers',
        topSkills: 'Top Skills',
        noSuperPowers: 'No Super Powers identified. Rate your skills higher to see them here.',

        // Progress
        progress: 'Progress',
        step: 'Step',
        of: 'of',

        // OnboardingStatusWidget
        onboardingStart: 'Start Onboarding',
        onboardingStartDescription: 'Welcome! Start your personal onboarding.',
        onboardingContinue: 'Continue Onboarding',
        onboardingContinueDescription: 'You have completed {progress}% of your onboarding.',
        onboardingCompleted: 'Onboarding Completed',
        onboardingCompletedDescription: 'Successfully completed',
        startNow: 'Start Now',
        continueOnboarding: 'Continue',
        viewResults: 'View Results',
        estimatedDuration: 'Estimated duration: ~11 minutes',
        pauseAnytime: 'You can pause anytime and continue later',
    },

    th: {
        // Navigation
        next: 'ถัดไป',
        back: 'กลับ',
        continue: 'ดำเนินการต่อ',
        finish: 'เสร็จสิ้น',
        cancel: 'ยกเลิก',

        // Steps
        welcome: 'ยินดีต้อนรับ',
        cvUpload: 'อัปโหลด CV',
        analysis: 'การวิเคราะห์',
        results: 'ผลลัพธ์',
        overview: 'ภาพรวม',
        completion: 'เสร็จสิ้น',

        // Personal Info
        personalInfo: 'ข้อมูลส่วนบุคคล',
        firstName: 'ชื่อ',
        lastName: 'นามสกุล',
        shortDescription: 'คำอธิบายสั้น',
        shortDescriptionPlaceholder: 'อธิบายตัวคุณเองสั้นๆ ใน 2-3 ประโยค',

        // Skills & Competences
        skills: 'ทักษะ',
        competences: 'ความสามารถ',
        certificates: 'ใบรับรอง',
        roles: 'บทบาท',
        suggestedSkills: 'ทักษะที่แนะนำ',
        suggestedCompetences: 'ความสามารถที่แนะนำ',
        suggestedCertificates: 'ใบรับรองที่แนะนำ',
        suggestedRoles: 'บทบาทที่แนะนำ',

        // Levels
        junior: 'จูเนียร์',
        midLevel: 'ระดับกลาง',
        senior: 'ซีเนียร์',
        expert: 'ผู้เชี่ยวชาญ',

        // Actions
        accept: 'ยอมรับ',
        reject: 'ปฏิเสธ',
        edit: 'แก้ไข',
        add: 'เพิ่ม',
        close: 'ปิด',
        save: 'บันทึก',
        confirm: 'ยืนยัน',

        // Messages
        noSkillsSelected: 'ไม่ได้เลือกทักษะ',
        noCompetencesSelected: 'ไม่ได้เลือกความสามารถ',
        noCertificatesSelected: 'ไม่ได้เลือกใบรับรอง',
        noRolesSelected: 'ไม่ได้เลือกบทบาท',

        // Agent Messages
        welcomeMessage: 'ยินดีต้อนรับสู่การปฐมนิเทศ! ฉันจะแนะนำคุณผ่านกระบวนการนี้',
        cvUploadMessage: 'อัปโหลด CV ของคุณเพื่อให้เราวิเคราะห์ทักษะของคุณได้',
        personalInfoMessage: 'เริ่มต้นด้วยข้อมูลส่วนบุคคลของคุณ กรุณากรอกชื่อให้ครบถ้วนและเพิ่มคำอธิบายสั้นๆ',
        skillsMessage: 'ยอดเยี่ยม! ตอนนี้มาดูทักษะที่ระบุไว้ ตรวจสอบข้อเสนอแนะและปรับระดับทักษะตามความสามารถของคุณ',
        superPowersMessage: 'สมบูรณ์แบบ! ตอนนี้เลือก SuperPowers ของคุณ เลือกทักษะที่แข็งแกร่งที่สุด 3 อย่างที่ทำให้คุณโดดเด่น',
        certificatesMessage: 'ดีมาก! นี่คือใบรับรองที่ได้รับการยอมรับ ยืนยันที่เกี่ยวข้องและเพิ่มเติมหากจำเป็น',
        rolesMessage: 'ยอดเยี่ยม! สุดท้ายคือบทบาทที่แนะนำ สิ่งเหล่านี้อิงจากทักษะและประสบการณ์ของคุณ',
        resultsMessage:
            'ยอดเยี่ยม! นี่คือภาพรวมความสามารถส่วนบุคคลของคุณ Super Powers ของคุณถูกเน้น คุณยังสามารถปรับแต่งได้ก่อนที่เราจะดำเนินการต่อ',

        // Tooltips & Aria Labels
        editSkillLevel: 'แก้ไขระดับทักษะ',
        acceptItem: 'ยอมรับรายการ',
        rejectItem: 'ปฏิเสธรายการ',
        uploadLogo: 'อัปโหลดโลโก้',
        selectDate: 'เลือกวันที่',
        expandSection: 'ขยายส่วน',
        collapseSection: 'ยุบส่วน',

        // Super Powers
        superPowers: 'พลังพิเศษ',
        topSkills: 'ทักษะชั้นนำ',
        noSuperPowers: 'ไม่พบ Super Powers ให้คะแนนทักษะของคุณสูงขึ้นเพื่อดูที่นี่',

        // Progress
        progress: 'ความคืบหน้า',
        step: 'ขั้นตอน',
        of: 'จาก',

        // OnboardingStatusWidget
        onboardingStart: 'เริ่มการปฐมนิเทศ',
        onboardingStartDescription: 'ยินดีต้อนรับ! เริ่มการปฐมนิเทศส่วนบุคคลของคุณ',
        onboardingContinue: 'ดำเนินการปฐมนิเทศต่อ',
        onboardingContinueDescription: 'คุณได้ทำการปฐมนิเทศเสร็จแล้ว {progress}%',
        onboardingCompleted: 'การปฐมนิเทศเสร็จสิ้น',
        onboardingCompletedDescription: 'เสร็จสิ้นเรียบร้อยแล้ว',
        startNow: 'เริ่มเลย',
        continueOnboarding: 'ดำเนินการต่อ',
        viewResults: 'ดูผลลัพธ์',
        estimatedDuration: 'ระยะเวลาโดยประมาณ: ~11 นาที',
        pauseAnytime: 'คุณสามารถหยุดชั่วคราวและดำเนินการต่อในภายหลังได้',
    },
};

// Hook for using onboarding strings
export const useOnboardingStrings = (language: 'de' | 'en' | 'th' = 'de'): OnboardingLangStrings => {
    return onboardingStrings[language];
};
